import {z} from 'zod';
import {isReleasedNewRegisterFlow} from '../../../config/unReleasePage';
import {WorkAttractiveConditionSchema} from '../company/schema';
import {DEGREE_TYPE_LIST} from '../university/constants';
import {GENDER_TYPE_LIST, JOB_HUNTING_STATUS_VALUE} from '../user/constants';

const isNewRegisterFlow = isReleasedNewRegisterFlow();

export const GetUserDataForAnalyticsResponse = z.object({
  id: z.number(),
  sex: z.number(),
  university: z.number(),
  major: z.number(),
  subject: z.number(),
  bun_ri: z.number(),
  grade: z.number(),
  graduation_year: z.number(),
  graduation_month: z.number(),
  current_univ_type: z.string(),
  user_registered: z.string(),
  university_txt: z.string(),
  major_txt: z.string(),
  subject_txt: z.string(),
  calculated_graduation_year: z.number(),
  sex_txt: z.string(),
  bun_ri_txt: z.string(),
  grade_txt: z.string(),
  current_univ_type_txt: z.string(),
  length_development_experience: z.number(),
  has_engineer_interest: z.boolean(),
});

export const GetUserMeScoutResponseSchema = z.object({
  count: z.number(),
});

export const GetUserMeInterestResponseSchema = z.object({
  count: z.number(),
});

export const GetUserProfileResponseSchema = z.object({
  profile_input_rate: z.number(),
  unfilled_fields: z.array(
    z.object({
      profile_edit_page_link: z.string(),
      unfilled_field_label: z.string(),
      required_action_label: z.string(),
      is_empty: z.boolean().optional(),
    }),
  ),
});

export const ZodEnumDegreeTypeSchema = z.enum([...DEGREE_TYPE_LIST, '']);
export const GenderTypeSchema = z.enum([...GENDER_TYPE_LIST, 'undefined']);
export const JobHuntingStatusSchema = z.enum([...JOB_HUNTING_STATUS_VALUE, '']);
export const UsersMeResponse = z.object({
  id: z.number(),
  isRegularUser: z.boolean(),
  isDemoUser: z.boolean(),
  isAgreedRule: z.boolean(),
  graduationYear: z.number(),
  rawGraduationYear: z.number(),
  rawGraduationMonth: z.number(),
  gradeChangeMonth: z.number(),
  communityNickname: z.string(),
  adviserUserName: z.string(),
  firstName: z.string(),
  familyName: z.string(),
  firstNameKana: z.string(),
  familyNameKana: z.string(),
  email: z.string(), // private_email
  birthday: z.string(),
  gender: GenderTypeSchema,
  grade: z.object({
    id: z.number(),
    name: z.string(),
  }),
  degreeType: ZodEnumDegreeTypeSchema,
  universityName: z.string(),
  majorName: z.string(),
  subjectName: z.string(),
  nextCheckDate: z.string(),
  checkYear: z.number(),
  isEmailConfirmed: z.boolean(), // private_emailが認証済か
  pendingEmail: z.string(), // 認証前の仮private_email
  isStudentVerified: isNewRegisterFlow // 大学認証が完了済みか
    ? z.boolean()
    : z.boolean().optional(),
  pendingAcEmail: z.string().optional(), // 認証前の仮大学メールアドレス
  isEngineer: z.boolean(),
  isForeignUniv: z.boolean(),
  inactive: z.boolean(),
  jobHuntingStatusCheckDate: z.string(),
  jobHuntingStatus: JobHuntingStatusSchema,
  // 基本情報(電話番号・メールアドレス・住所)の定期更新が必要か。
  // BEが未リリース・切り戻し中でもパースを落とさないよう、欠けていたら更新不要として扱う
  isBasicInfoCheckRequired: z.boolean().optional(),
});

const BackgroundImageURLs = z.object({
  '300x126': z.string(),
  '640x268': z.string(),
  '948x392': z.string(),
});

const LogoURLs = z.object({
  '140x105': z.string(),
  '200x150': z.string(),
  '400x300': z.string(),
});

export const CompaniesSchema = z.object({
  backgroundImageURLs: BackgroundImageURLs,
  capitalDescription: z.string(),
  catchPhrase: z.string(),
  employeeCount: z.number(),
  followerCount: z.number(),
  foundedYear: z.number(),
  id: z.number(),
  industry: z.string(),
  industryCategoryID: z.number(),
  isFollowed: z.boolean(),
  isNew: z.boolean(),
  isHiring: z.boolean(),
  isWishedIndustry: z.boolean(),
  isWishedJobType: z.boolean(),
  logoURLs: LogoURLs,
  name: z.string(),
  pageType: z.string(),
  studyUrl: z.string(),
  workAttractiveConditions: z.array(WorkAttractiveConditionSchema).optional(),
});

export const UsersMeFollowedCompaniesResponse = z.object({
  companies: z.array(CompaniesSchema),
  nextPage: z.number(),
  totalCount: z.number(),
});

// ポップアップメッセージ
const PopupMessageSchema = z.object({
  messageType: z.string(),
});

export const GetPopupMessageResponse = PopupMessageSchema;

export const PutPopupMessageResponse = z.object({
  messageType: z.string(),
});

export const FollowAdCompanyListSchema = z.object({
  companies: z.array(CompaniesSchema),
});

// エントリーフォーム情報取得
const EntryFormScheduleSchema = z.object({
  id: z.number(),
  name: z.string(),
  venue: z.string(),
  startTime: z.string(),
  endTime: z.string(),
});

const EntryFormQuestionAndAnswersSchema = z.object({
  question: z.string(),
  answerType: z.string(),
  answer: z.string(),
  selectedOptions: z.array(z.string()),
  order: z.number(),
});

export const GetUsersMeEntryFormResponseSchema = z.object({
  id: z.number(),
  title: z.string(),
  companyName: z.string(),
  industryID: z.number(),
  type: z.string(),
  canCancel: z.boolean(),
  schedule: EntryFormScheduleSchema,
  questionsAndAnswers: z.array(EntryFormQuestionAndAnswersSchema),
  isSetFileUpload: z.boolean(),
});

// 自分が応募したエントリーフォーム一覧
export const GetUsersMeEntryFormsResponseSchema = z.array(
  z.object({
    id: z.number(),
    title: z.string(),
    companyName: z.string(),
    type: z.string(),
    isSetFileUpload: z.boolean(),
  }),
);

export const DeleteEntryFormResponseSchema = z.string().nullable();

// 休会にする・休会を解除する
export const PutUserInactiveResponseSchema = z.string().nullable();

// 就活状況を更新する
export const PutJobHuntingStatusRequestSchema = z.enum([
  ...JOB_HUNTING_STATUS_VALUE,
]);
export const PutJobHuntingStatusResponseSchema = z.string().nullable();

// 基本情報の定期更新を完了したことを通知する
export const PutBasicInfoCheckedResponseSchema = z.string().nullable();
